type KeysOf<T> = keyof T
type ValuesOf<T> = T[keyof T]

const roles = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
} as const
export type RoleTypes = ValuesOf<typeof roles>

export type LuzPlanFeatures = Record<
  | "addRemoveCompetitors" // the ability to add and remove competitors
  | "apiTokens" // the ability to generate tokens to hit the API programatically
  | "benchmarking" // tool to benchmark competitors and products
  | "businessIntelligence" // can view My Store BI dashboards/visualizations
  | "demandPlanning" // access to tool for demand planning
  | "exportData" // has access on Reports tab to download csv's of a company's data
  | "internalTools" // can view internal tools tab and perform ops like actions
  | "lifetimeValue" // can view lifetime value tool
  | "marketingData" // can view all marketing data like emails and social media
  | "marketInsights" // the ability to use the market insights tool
  | "historicalData" // can view all historical data (instead of just 30 days) for companies they pay for
  | "pixelAttribution" // can view data related to the Luz pixel integration
  | "premiumOverview" // whether they get a premium view of EVERY overview page
  | "productsView" // the ability to see ALL product pages
  | "nonMerchant", // gets some special access and a different UI since they don't care about product data
  boolean
>
export type PlanFeatureTypes = keyof LuzPlanFeatures

const opsLitePackageFeatures = [
  "pixelAttribution",
  "businessIntelligence",
  "demandPlanning",
  "lifetimeValue",
]
const productLitePackageFeatures = [
  "addRemoveCompetitors",
  "benchmarking",
  "marketingData",
]
const productProPackageFeatures = [
  ...productLitePackageFeatures,
  "exportData",
  "historicalData",
  "apiTokens",
  "marketInsights",
]
const nonMerchantPackageFeatures = [
  "addRemoveCompetitors",
  "marketingData",
  "exportData",
  "historicalData",
  "apiTokens",
  "marketInsights",
  "nonMerchant",
]
const luzPackages = {
  opsLite: opsLitePackageFeatures,
  productLite: productLitePackageFeatures,
  productPro: productProPackageFeatures,
  nonMerchant: nonMerchantPackageFeatures,
}

const luzFeatures: PlanFeatureTypes[] = [
  "addRemoveCompetitors",
  "benchmarking",
  "marketingData",
  "exportData",
  "historicalData",
  "apiTokens",
  "marketInsights",
  "businessIntelligence",
  "demandPlanning",
  "lifetimeValue",
  "pixelAttribution",
  "premiumOverview",
  "productsView",
  "nonMerchant",
]

interface LuzPlanType extends LuzPlanFeatures {
  name: string
  competitorLimit: number // default number of competitors they can add to track (should be mutable by Ops)
  addRemoveCompetitorQuota: number // how many comps they can add/remove each month
}
type LuzPlansType = Record<
  | "demo"
  | "retail-starter"
  | "retail-pro"
  | "retail-plus"
  | "nonretail"
  | "internal",
  LuzPlanType
>

const luzPlans: LuzPlansType = {
  demo: {
    name: "Demo",
    competitorLimit: 0,
    addRemoveCompetitors: false,
    addRemoveCompetitorQuota: 0,
    marketInsights: false,
    historicalData: false,
    marketingData: false,
    exportData: false,
    apiTokens: false,
    premiumOverview: false,
    productsView: false,
    internalTools: false,
    benchmarking: false,
    businessIntelligence: false,
    demandPlanning: false,
    lifetimeValue: false,
    pixelAttribution: false,
    nonMerchant: false,
  },
  "retail-starter": {
    name: "Starter",
    competitorLimit: 3,
    addRemoveCompetitors: true,
    addRemoveCompetitorQuota: 1,
    marketInsights: true,
    historicalData: false,
    marketingData: true,
    exportData: false,
    apiTokens: false,
    premiumOverview: false,
    productsView: false,
    internalTools: false,
    benchmarking: false,
    businessIntelligence: false,
    demandPlanning: false,
    lifetimeValue: false,
    pixelAttribution: false,
    nonMerchant: false,
  },
  "retail-pro": {
    name: "Pro",
    competitorLimit: 6,
    addRemoveCompetitors: true,
    addRemoveCompetitorQuota: 1,
    marketInsights: true,
    historicalData: true,
    marketingData: true,
    exportData: false,
    apiTokens: false,
    premiumOverview: false,
    productsView: false,
    internalTools: false,
    benchmarking: false,
    businessIntelligence: false,
    demandPlanning: false,
    lifetimeValue: false,
    pixelAttribution: false,
    nonMerchant: false,
  },
  "retail-plus": {
    name: "Plus",
    competitorLimit: 10,
    addRemoveCompetitors: true,
    addRemoveCompetitorQuota: 1,
    marketInsights: true,
    historicalData: true,
    marketingData: true,
    exportData: true,
    apiTokens: true,
    premiumOverview: false,
    productsView: false,
    internalTools: false,
    benchmarking: false,
    businessIntelligence: false,
    demandPlanning: false,
    lifetimeValue: false,
    pixelAttribution: false,
    nonMerchant: false,
  },
  nonretail: {
    name: "Nonretail",
    competitorLimit: 25,
    addRemoveCompetitors: true,
    addRemoveCompetitorQuota: 9999,
    marketInsights: true,
    historicalData: true,
    marketingData: true,
    exportData: true,
    apiTokens: true,
    premiumOverview: true,
    productsView: false,
    internalTools: false,
    benchmarking: false,
    businessIntelligence: false,
    demandPlanning: false,
    lifetimeValue: false,
    pixelAttribution: false,
    nonMerchant: true,
  },
  internal: {
    name: "Internal",
    competitorLimit: 9999,
    addRemoveCompetitors: true,
    addRemoveCompetitorQuota: 9999,
    marketInsights: true,
    historicalData: true,
    marketingData: true,
    exportData: true,
    apiTokens: true,
    premiumOverview: true,
    productsView: true,
    internalTools: true,
    benchmarking: true,
    businessIntelligence: true,
    demandPlanning: true,
    lifetimeValue: true,
    pixelAttribution: true,
    nonMerchant: true,
  },
}
export type LuzPlanNameTypes = KeysOf<typeof luzPlans>

export { roles, luzPlans, luzPackages, luzFeatures }
